// Service / supply harbours used by the aqua fleet — quays where vessels bunker,
// take on feed, lie at berth or get maintained. Source /v1/shore-sites, backed
// by the shore_sites table (clustered AIS dwell). This is the HEURISTIC
// companion to the authoritative Landanlegg (onshore.ts) layer: suggestive, not
// an official port list — so it gets one distinct ISOTYPE symbol (an anchor, in
// steel blue) and the popup is explicit about the inferred basis.

import maplibregl from "maplibre-gl";

import { addPin, pinSVG } from "./pins";

const API_BASE = import.meta.env.VITE_API_BASE;
const SERVICE_LAYERS = ["service-pin"];
// Brand palette: the heuristic service layer is an *inverted* token — Deep fill
// with a Signal-mint ring + mint anchor — so it reads as the lighter-weight,
// inferred sibling of the solid-mint slaughter pins, distinguished by glyph.
const DEEP = "#082E22";
const MINT = "#B4D9C7";
let popup: maplibregl.Popup | null = null;

const KLASS_NO: Record<string, string> = {
  service: "Servicekai",
  berth: "Kai / liggeplass",
  feed: "Fôrkai",
  mixed: "Blandet bruk",
};

export const SERVICE_META = { label: "Servicehavn", color: MINT };

function esc(s: string | null): string {
  return (s ?? "").replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

export function anchorGlyph(ink: string): string {
  // Flat anchor (ring · shank · stock · fluke arc), centred on the origin.
  return (
    `<g fill="none" stroke="${ink}" stroke-width="2.6" ` +
    `stroke-linecap="round" stroke-linejoin="round">` +
    `<circle cx="0" cy="-11" r="3"/>` +
    `<path d="M0 -8 L0 12"/>` +
    `<path d="M-7 -3 L7 -3"/>` +
    `<path d="M-9 3 Q-9 12 0 12 Q9 12 9 3"/>` +
    `</g>`
  );
}

export async function initServicePorts(map: maplibregl.Map): Promise<void> {
  let data: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };
  try {
    const res = await fetch(`${API_BASE}/v1/shore-sites`, { cache: "no-store" });
    if (res.ok) data = (await res.json()) as GeoJSON.FeatureCollection;
  } catch {
    /* leave the layer empty if the feed is unavailable */
  }

  if (map.getSource("serviceports")) {
    (map.getSource("serviceports") as maplibregl.GeoJSONSource).setData(data);
    return;
  }

  await addPin(map, "pin-service", pinSVG(DEEP, MINT, anchorGlyph(MINT)));

  // Cluster on zoom-out → overview of where the harbours bunch up; bubbles are
  // sized by harbour count and expand on click.
  map.addSource("serviceports", {
    type: "geojson",
    data,
    cluster: true,
    clusterRadius: 48,
    clusterMaxZoom: 9,
  });
  map.addLayer({
    id: "service-pin",
    type: "symbol",
    source: "serviceports",
    filter: ["!", ["has", "point_count"]], // individual harbours; clusters are HTML bubbles
    layout: {
      visibility: "none", // opt-in
      "icon-image": "pin-service",
      // Size carries traffic (distinct vessels seen), same as the slaughter pins.
      "icon-size": [
        "interpolate", ["linear"], ["coalesce", ["get", "n_vessels"], 0],
        0, 0.85, 8, 1.0, 20, 1.25, 40, 1.5,
      ],
      "icon-anchor": "center",
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
    },
  });

  map.on("click", "service-pin", (e) => {
    const f = e.features?.[0];
    if (!f) return;
    const p = f.properties as {
      klass: string; place: string | null;
      n_vessels: number | null; n_wellboats: number | null;
    };
    const coords = (f.geometry as GeoJSON.Point).coordinates.slice() as [number, number];
    popup?.remove();
    const traffic = [
      p.n_vessels != null ? `${p.n_vessels} fartøy` : null,
      p.n_wellboats ? `${p.n_wellboats} brønnbåt${p.n_wellboats === 1 ? "" : "er"}` : null,
    ].filter(Boolean).join(" · ");
    popup = new maplibregl.Popup({ className: "onshore-tip", offset: 24, maxWidth: "240px" })
      .setLngLat(coords)
      .setHTML(
        `<div class="popup-label">${esc(KLASS_NO[p.klass] ?? "Servicehavn")}</div>` +
          `<div class="onshore-name">${esc(p.place) || "Ukjent sted"}</div>` +
          (traffic ? `<div class="onshore-meta">${traffic}</div>` : "") +
          `<div class="onshore-meta" style="opacity:.6;margin-top:6px">Avledet fra AIS-mønstre</div>`,
      )
      .addTo(map);
  });
  map.on("mouseenter", "service-pin", () => (map.getCanvas().style.cursor = "pointer"));
  map.on("mouseleave", "service-pin", () => (map.getCanvas().style.cursor = ""));
}

export function setServicePortsVisible(map: maplibregl.Map, on: boolean): void {
  for (const id of SERVICE_LAYERS) {
    if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", on ? "visible" : "none");
  }
}
